import { useEffect, useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import { markCinemaConfirmAnswered, wasCinemaConfirmAnsweredRecently } from "../../lib/cinemaConfirmStorage"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

type CinemaConfirmHintProps = {
    cinemaId: number
    label: string
    cinemaName: string
    cityName: string
    address?: string
    onYes: () => void
    onNo: () => void
}

export default function CinemaConfirmHint({
    cinemaId,
    label,
    cinemaName,
    cityName,
    address,
    onYes,
    onNo,
}: CinemaConfirmHintProps) {
    const [open, setOpen] = useState(false)

    useEffect(() => {
        if (!wasCinemaConfirmAnsweredRecently(cinemaId)) {
            setOpen(true)
        }
    }, [cinemaId])

    const handleAnswer = (action: () => void) => {
        markCinemaConfirmAnswered(cinemaId)
        setOpen(false)
        action()
    }

    const handleYes = () => handleAnswer(onYes)

    const handleNo = () => handleAnswer(onNo)

    return (
        <div className="relative flex flex-col items-start gap-px">
            <span
                className="text-[10px] uppercase tracking-[0.06em] text-white/40"
                style={{ fontFamily: MONO, fontWeight: 700 }}>
                {label}
            </span>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="cursor-pointer text-left text-[13px] leading-tight text-[#349be6] transition hover:brightness-110"
                style={{
                    fontFamily: BODY,
                    fontWeight: 700,
                    textShadow: "0 0 14px rgba(52,155,230,0.35)",
                }}>
                {cinemaName}
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -6, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.97 }}
                        transition={{ type: "spring", stiffness: 420, damping: 28 }}
                        className="absolute left-0 top-full z-50 mt-2 w-[min(calc(100vw-2.5rem),260px)] overflow-hidden rounded-[16px] border border-white/12 shadow-[0_16px_48px_rgba(0,0,0,0.45)]"
                        style={{ background: "linear-gradient(160deg, #1e2f58 0%, #1a1438 100%)" }}>
                        <div
                            className="absolute -top-1.5 left-4 size-3 rotate-45 border-l border-t border-white/12"
                            style={{ background: "#1e2f58" }}
                        />

                        <div className="px-4 py-3.5">
                            <p
                                className="text-[14px] leading-snug text-white"
                                style={{ fontFamily: DISPLAY, fontWeight: 700, letterSpacing: "-0.01em" }}>
                                Ваш кинотеатр?
                            </p>
                            <p className="mt-1 text-[13px] leading-snug text-white/55" style={{ fontFamily: BODY, fontWeight: 500 }}>
                                {cityName}, {cinemaName}
                            </p>
                            {address && (
                                <p className="mt-1 text-[12px] leading-snug text-white/40" style={{ fontFamily: BODY, fontWeight: 500 }}>
                                    {address}
                                </p>
                            )}
                        </div>

                        <div className="grid grid-cols-2 border-t border-white/10">
                            <button
                                type="button"
                                onClick={handleNo}
                                className="cursor-pointer py-3 text-[13px] font-bold text-white/70 transition hover:bg-white/[0.06]"
                                style={{ fontFamily: MONO }}>
                                Нет
                            </button>
                            <button
                                type="button"
                                onClick={handleYes}
                                className="cursor-pointer border-l border-white/10 py-3 text-[13px] font-bold text-[#349be6] transition hover:bg-white/[0.06]"
                                style={{ fontFamily: MONO }}>
                                Да
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
